<template>
  <div class="tape-layer-three-d">
    <div ref="container" class="container"></div>
    <div class="disclaimer">{{ t('pages.products.tape.disclaimer3d') }}</div>
    <div v-if="activeLayer" class="layer-info">
      <h3>{{ activeLayer.name }}</h3>
      <div class="info-content">
        <div
          class="color-box"
          :style="{ backgroundColor: activeLayer.color }"
        ></div>
        <div class="layer-details">
          <!-- <p>{{ activeLayer.description }}</p> -->
          <p v-if="activeLayer.thickness">
            {{ activeLayer.thickness }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const { t } = useI18n();

// 接口定义
interface TapeLayer {
  id: number;
  name: string;
  description: string;
  thickness: string;
  color: string;
  threeObject?: THREE.Mesh;
}

// 引用和状态
const container = ref<HTMLElement | null>(null);
const activeLayer = ref<TapeLayer | null>(null);
const layers = ref<TapeLayer[]>([
  {
    "id": 0,
    "name": "Cu", // 原 ID=10 的 name
    "description": "Cu", // 原 ID=10 的 description
    "thickness": "", // 原 ID=10 的 thickness
    "color": "#c58f80" // 保留原颜色
  },
  {
    "id": 1,
    "name": "Ag", // 原 ID=2 的 name
    "description": "Ag", // 原 ID=2 的 description
    "thickness": "~1.5-2 μm", // 原 ID=2 的 thickness
    "color": "#f8f8f8" // 保留原颜色
  },
  {
    "id": 2,
    "name": "Hastelloy Substrate", // 原 ID=9 的 name
    "description": "Hastelloy Substrate", // 原 ID=9 的 description
    "thickness": "30 or 50 μm", // 原 ID=9 的 thickness
    "color": "#b9b8b8" // 保留原颜色
  },
  {
    "id": 3,
    "name": "Al₂O₃", // 原 ID=8 的 name
    "description": "Al₂O₃", // 原 ID=8 的 description
    "thickness": "~100 nm", // 原 ID=8 的 thickness（注意单位从 μm 改为 nm，原数据中 ID=8 是 nm）
    "color": "#d9d9d9" // 保留原颜色
  },
  {
    "id": 4,
    "name": "Y₂O₃", // 原 ID=7 的 name
    "description": "Y₂O₃", // 原 ID=7 的 description
    "thickness": "~15 nm", // 原 ID=7 的 thickness
    "color": "#d9b9ad" // 保留原颜色
  },
  {
    "id": 5,
    "name": "MgO", // 原 ID=6 的 name
    "description": "MgO", // 原 ID=6 的 description
    "thickness": "~5 nm", // 原 ID=6 的 thickness
    "color": "#ededed" // 保留原颜色
  },
  {
    "id": 6,
    "name": "LaMnO₃", // 原 ID=5 的 name
    "description": "LaMnO₃", // 原 ID=5 的 description
    "thickness": "~3 nm", // 原 ID=5 的 thickness
    "color": "#efefef" // 保留原颜色
  },
  {
    "id": 7,
    "name": "CeO₂", // 原 ID=4 的 name
    "description": "CeO₂", // 原 ID=4 的 description
    "thickness": "~200 nm", // 原 ID=4 的 thickness
    "color": "#d0e0fb" // 保留原颜色
  },
  {
    "id": 8,
    "name": "REBCO Superconducting Layer", // 原 ID=3 的 name
    "description": "REBCO Superconducting Layer", // 原 ID=3 的 description
    "thickness": "~2.5-3 μm", // 原 ID=3 的 thickness
    "color": "#46a4f1" // 保留原颜色
  },
  {
    "id": 9,
    "name": "Ag", // 原 ID=2 的 name
    "description": "Ag", // 原 ID=2 的 description
    "thickness": "~2um", // 原 ID=2 的 thickness
    "color": "#f8f8f8" // 保留原颜色
  },
  {
    "id": 10,
    "name": "Cu", // 原 ID=1 的 name
    "description": "Cu", // 原 ID=1 的 description
    "thickness": "", // 原 ID=1 的 thickness
    "color": "#c58f80" // 保留原颜色
  }
]);

// Three.js 相关变量
let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let controls: OrbitControls;
let raycaster: THREE.Raycaster;
let mouse: THREE.Vector2;
let animationFrameId: number | null = null;
let textureLoader: THREE.TextureLoader;
let metalTexture: THREE.Texture | null = null;

// 定义一个全局变量来存储总高度
let totalHeight = 0;

// 初始化 Three.js
const initThreeJS = () => {
  // 创建无边框场景
  scene = new THREE.Scene();
  
  // 初始化纹理加载器并加载金属纹理
  textureLoader = new THREE.TextureLoader();
  metalTexture = textureLoader.load('/images/金属材质纹理贴图.png');
  
  // 设置纹理重复模式和缩放
  if (metalTexture) {
    metalTexture.wrapS = THREE.RepeatWrapping;
    metalTexture.wrapT = THREE.RepeatWrapping;
    metalTexture.repeat.set(8, 4); // 在X轴重复8次，Y轴重复4次，让纹理变小
  }
  
  // 创建相机
  const width = container.value!.clientWidth;
  const height = container.value!.clientHeight;
  camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 1000);
  camera.position.set(
    -10, // 增加距离以适应更长的模型
    20.700909766198689,
    8 // 稍微增加z轴距离
  );
  
  // 创建渲染器
  renderer = new THREE.WebGLRenderer({ 
    antialias: true,
    alpha: true // 启用透明背景
  });
  renderer.setSize(width, height);
  renderer.shadowMap.enabled = true;
  renderer.setClearColor(0xffffff, 0.6); // 设置背景为半透明白色
  
  // 设置正确的色彩管理
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  
  container.value!.appendChild(renderer.domElement);

  // 创建控制器
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  // 禁用所有交互，仅保留 hover 检测
  controls.enableRotate = false;
  controls.enableZoom = false;
  controls.enablePan = false;
  controls.enabled = true;

  // 创建光源 - 多光源系统展现金属光泽
  const ambientLight = new THREE.AmbientLight(0xffffff, 1); // 降低到合理范围，避免过度曝光
  scene.add(ambientLight);

  // 主光源 - 从上方照射
  const mainLight = new THREE.DirectionalLight(0xffffff, 1.5);
  mainLight.position.set(0, 30, 10);
  mainLight.castShadow = false;
  scene.add(mainLight);

  // 左侧光源 - 暖色调
  const leftLight = new THREE.DirectionalLight(0xfff8dc, 1.2); // 淡黄色
  leftLight.position.set(-5, 5, 5);
  scene.add(leftLight);

  // 右侧光源 - 冷色调
  const rightLight = new THREE.DirectionalLight(0xe6f3ff, 1.2); // 淡蓝色
  rightLight.position.set(5, 5, 5);
  scene.add(rightLight);

  // 前方光源 - 增强正面亮度
  const frontLight = new THREE.DirectionalLight(0xffffff, 0.8);
  frontLight.position.set(0, 10, 20);
  scene.add(frontLight);

  // 后方光源 - 创造边缘光效果
  const backLight = new THREE.DirectionalLight(0xffffff, 0.6);
  backLight.position.set(0, 5, -15);
  scene.add(backLight);

  // 顶部偏左光源
  const topLeftLight = new THREE.DirectionalLight(0xffffcc, 0.7); // 淡暖黄
  topLeftLight.position.set(-15, 10, 0);
  scene.add(topLeftLight);

  // 顶部偏右光源
  const topRightLight = new THREE.DirectionalLight(0xccffff, 0.7); // 淡冷蓝
  topRightLight.position.set(15, 10, 0);
  scene.add(topRightLight);

  // 底部补光
  const bottomLight = new THREE.DirectionalLight(0xffffff, 0.3);
  bottomLight.position.set(0, -10, 5);
  scene.add(bottomLight);

  // 添加几个点光源创造局部高光
  const pointLight1 = new THREE.PointLight(0xffffff, 0.8, 30);
  pointLight1.position.set(10, 10, 15);
  scene.add(pointLight1);

  const pointLight2 = new THREE.PointLight(0xfff8dc, 0.6, 25);
  pointLight2.position.set(-8, 10, 12);
  scene.add(pointLight2);

  const pointLight3 = new THREE.PointLight(0xe6f3ff, 0.6, 25);
  pointLight3.position.set(8, 10, -8);
  scene.add(pointLight3);

  // 设置射线放射器用于检测鼠标交互
  raycaster = new THREE.Raycaster();
  mouse = new THREE.Vector2();

  // 创建带材模型
  createTapeModel();

  // 添加事件监听
  window.addEventListener("resize", handleResize);
  if (container.value) {
    container.value.addEventListener("mousemove", handleMouseMove);
    container.value.addEventListener("click", handleClick);
  }

  // 开始动画循环
  animate();

  // 初始化完成后飞至俯视状态
  setTimeout(() => {
    flyToTopView();
  }, 500);
};

// 创建带材的3D模型
const createTapeModel = () => {
  // 计算总厚度，以便确定层的位置
  const baseWidth = 20; // 增加长度（从10增加到20）
  const baseDepth = 2; // 减少宽度（从4减少到2）

  // 累积偏移量，用于定位各层
  let offset = 0;

  // 创建每一层
  layers.value.forEach((layer: TapeLayer, index: number) => {
    // 根据层的类型设置不同的厚度（高度）
    let thickness = 0.15; // 稍微减少厚度让层次更明显

    // 根据索引减小宽度，创建阶梯状结构，调整宽度计算逻辑使底部对齐
    const stepFactor = 0.065; // 减小阶梯因子
    const stepOffset = index * stepFactor;
    const width = baseWidth - stepOffset * baseWidth;
    const depth = baseDepth; // 深度保持一致，不再递减

    // 创建几何体
    const geometry = new THREE.BoxGeometry(width, thickness, depth);

    // 创建材质 - 增强金属效果
    const layerTexture = metalTexture?.clone();
    if (layerTexture) {
      layerTexture.wrapS = THREE.RepeatWrapping;
      layerTexture.wrapT = THREE.RepeatWrapping;
      // 根据层的尺寸调整纹理重复
      const repeatX = Math.max(1, width / 20); // 根据宽度调整X轴重复
      const repeatY = Math.max(5, depth / 50); // 根据深度调整Y轴重复
      layerTexture.repeat.set(repeatX, repeatY);
    }

    const material = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(layer.color), // 直接使用原始颜色，不做倍增处理
      // map: layerTexture, // 应用金属纹理贴图
      transparent: true,
      opacity: 0.98,
      metalness: 0.7, // 降低金属度，让颜色更明显
      roughness: 0.15, // 增加粗糙度，让颜色更自然
      envMapIntensity: 1.5, // 降低环境映射强度
      clearcoat: 0.3, // 降低清漆效果
      clearcoatRoughness: 1, // 增加清漆粗糙度
      reflectivity: 0.5, // 降低反射率
    });

    // 创建网格
    const mesh = new THREE.Mesh(geometry, material);

    // 设置位置（从下往上堆叠），调整位置计算逻辑
    mesh.position.y = offset + thickness / 2;
    mesh.position.x = (baseWidth - width) / 2; // 调整x位置使底部对齐

    // 更新偏移量
    offset += thickness;

    // 启用阴影
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // 保存对网格的引用
    layer.threeObject = mesh;

    // 将网格添加到场景
    scene.add(mesh);
  });

  // 调整相机位置以便看到整个模型
  camera.position.y = offset / 2;
  controls.target.set(0, offset / 2, 0);
  controls.update();
  
  // 保存总高度供其他函数使用
  totalHeight = offset;
};

// 添加飞至俯视视角的函数
const flyToTopView = () => {
  const targetPosition = {
    x: 0, // 调整以适应更长的模型
    y: 4,
    z: 8 // 增加距离以看到完整的细长模型
  };
  
  const duration = 1500; // 动画持续时间（毫秒）
  const startTime = Date.now();
  const startPosition = {
    x: camera.position.x,
    y: camera.position.y,
    z: camera.position.z
  };
  
  // 目标看向点
  const targetLookAt = new THREE.Vector3(0, totalHeight / 2, 0);
  
  const animateCamera = () => {
    const elapsedTime = Date.now() - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    
    // 使用easeOutQuad缓动函数
    const easeProgress = 1 - (1 - progress) * (1 - progress);
    
    // 更新相机位置
    camera.position.x = startPosition.x + (targetPosition.x - startPosition.x) * easeProgress;
    camera.position.y = startPosition.y + (targetPosition.y - startPosition.y) * easeProgress;
    camera.position.z = startPosition.z + (targetPosition.z - startPosition.z) * easeProgress;
    
    // 更新控制器目标
    controls.target.lerp(targetLookAt, easeProgress);
    controls.update();
    
    if (progress < 1) {
      requestAnimationFrame(animateCamera);
    }
  };
  
  animateCamera();
};

// 处理窗口调整大小
const handleResize = () => {
  if (!container.value) return;

  const width = container.value.clientWidth;
  const height = container.value.clientHeight;

  camera.aspect = width / height;
  camera.updateProjectionMatrix();

  renderer.setSize(width, height);
};

// 处理鼠标移动
const handleMouseMove = (event: MouseEvent) => {
  if (!container.value) return;

  const rect = container.value.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / container.value.clientWidth) * 2 - 1;
  mouse.y =
    -((event.clientY - rect.top) / container.value.clientHeight) * 2 + 1;

  checkIntersection();
};

// 处理点击事件
const handleClick = () => {
  if (activeLayer.value) {
    console.log("Layer clicked:", activeLayer.value.name);

    // 可以添加点击后的特殊效果，例如突出显示或动画
    if (activeLayer.value.threeObject) {
      const originalPosition = activeLayer.value.threeObject.position.clone();

      // 创建一个向上移动的动画
      const animateHighlight = () => {
        const mesh = activeLayer.value?.threeObject;
        if (!mesh) return;

        // 向上移动一点
        mesh.position.y += 0.05;

        // 延迟后复位
        setTimeout(() => {
          if (mesh) {
            mesh.position.copy(originalPosition);
          }
        }, 300);
      };

      animateHighlight();
    }
  }
};

// 检查鼠标与对象的相交
const checkIntersection = () => {
  // 更新射线
  raycaster.setFromCamera(mouse, camera);

  // 获取所有可能相交的对象
  const intersectObjects = layers.value
    .filter((layer: TapeLayer) => layer.threeObject !== undefined)
    .map((layer: TapeLayer) => layer.threeObject) as THREE.Object3D[];

  // 计算相交
  const intersects = raycaster.intersectObjects(intersectObjects);

  if (intersects.length > 0) {
    // 找到相交的对象
    const intersectedObject = intersects[0].object;

    // 查找对应的层
    const intersectedLayer = layers.value.find(
      (layer: TapeLayer) => layer.threeObject === intersectedObject
    );

    if (intersectedLayer && intersectedLayer !== activeLayer.value) {
      // 更新活动层
      activeLayer.value = intersectedLayer;

      // 突出显示该层
      highlightLayer(intersectedLayer);
    }
  } else if (activeLayer.value) {
    // 如果鼠标没有悬停在任何层上，重置突出显示
    resetHighlights();
    activeLayer.value = null;
  }
};

// 突出显示一个层
const highlightLayer = (layer: TapeLayer) => {
  // 首先重置所有层
  resetHighlights();

  // 突出显示选中的层
  if (layer.threeObject) {
    const material = layer.threeObject.material as THREE.MeshPhysicalMaterial;
    material.emissive = new THREE.Color(0x333333);
    material.opacity = 1;
  }
};

// 重置所有层的突出显示
const resetHighlights = () => {
  layers.value.forEach((layer: TapeLayer) => {
    if (layer.threeObject) {
      const material = layer.threeObject.material as THREE.MeshPhysicalMaterial;
      material.emissive = new THREE.Color(0x000000);
      material.opacity = 1;
    }
  });
};

// 动画循环
const animate = () => {
  animationFrameId = requestAnimationFrame(animate);

  // 更新控制器
  controls.update();

  // 渲染场景
  renderer.render(scene, camera);
};

// 组件挂载
onMounted(() => {
  initThreeJS();
});

// 组件卸载前清理
onBeforeUnmount(() => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }

  if (container.value) {
    container.value.removeEventListener("mousemove", handleMouseMove);
    container.value.removeEventListener("click", handleClick);
  }

  window.removeEventListener("resize", handleResize);

  // 清理Three.js资源
  layers.value.forEach((layer: TapeLayer) => {
    if (layer.threeObject) {
      if (layer.threeObject.geometry) {
        layer.threeObject.geometry.dispose();
      }
      if (layer.threeObject.material) {
        const material = layer.threeObject.material as THREE.MeshPhysicalMaterial;
        // 清理克隆的纹理
        if (material.map) {
          material.map.dispose();
        }
        material.dispose();
      }
      scene.remove(layer.threeObject);
    }
  });

  // 清理纹理资源
  if (metalTexture) {
    metalTexture.dispose();
  }

  if (renderer) {
    renderer.dispose();
  }
});
</script>

<style scoped>
.tape-layer-three-d {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.container {
  width: 100%;
  height: 100%;
  min-height: 350px;
  /* background-color: #f9f9f9; */
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.layer-info {
  position: absolute;
  right: 20px;
  top: 20px;
  width: 250px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.layer-info h3 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
  font-size: 16px;
}

.info-content {
  display: flex;
  align-items: flex-start;
}

.color-box {
  width: 20px;
  height: 20px;
  margin-right: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  flex-shrink: 0;
}

.layer-details {
  flex: 1;
}

.layer-details p {
  margin: 5px 0;
  font-size: 14px;
  color: #555;
}

.disclaimer {
  position: absolute;
  left: 12px;
  bottom: 10px;
  right: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
</style>
